import React from 'react';
import { useTranslation } from 'react-i18next';

import { checkNoError, copyUrlInfo, copyUrls, validateActivationExpiry, UrlInfo } from '@/features/channels/channelsSlice';

import FlexRow from './FlexRow';
import Input, { TextArea } from './Input';

export function expiryToLocalDateTime(expiresAt?: number): string {
  if (!expiresAt || expiresAt <= 0)
    return '';
  const date = new Date(expiresAt * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    date.getFullYear() + '-' +
    pad(date.getMonth() + 1) + '-' +
    pad(date.getDate()) + 'T' +
    pad(date.getHours()) + ':' +
    pad(date.getMinutes())
  );
}

export function localDateTimeToExpiry(value: string): number {
  if (!value)
    return 0;
  const ts = new Date(value).getTime();
  if (isNaN(ts))
    return 0;
  return Math.floor(ts / 1000);
}

export function formatExpiryDate(expiresAt: number): string {
  return new Date(expiresAt * 1000).toLocaleString();
}

export function getExpiryCountdown(expiresAt: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = expiresAt - now;
  if (diff <= 0)
    return '';
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  if (days > 0)
    return `${days}d ${hours}h`;
  if (hours > 0)
    return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export interface InlineExpiryPickerProps {
  expiresAt:			number;
  setExpiresAt:			(ts: number) => any;
  isReadOnly?:			boolean;
}

export const InlineExpiryPicker = (props: InlineExpiryPickerProps) => {
  const { t } = useTranslation();

  const expiresAt = props.expiresAt || 0;
  const hasExpiry = expiresAt > 0;
  const nowSec = Math.floor(Date.now() / 1000);
  const isFuture = expiresAt > nowSec;

  const [expanded, setExpanded] = React.useState(hasExpiry);

  React.useEffect(() => {
    if (hasExpiry)
      setExpanded(true);
  }, [hasExpiry]);

  const toggleExpanded = () => {
    if (props.isReadOnly)
      return;
    setExpanded((v) => !v);
  };

  const onChange = (val: string) => {
    if (props.isReadOnly)
      return;
    props.setExpiresAt(localDateTimeToExpiry(val));
  };

  const iconClass = hasExpiry
    ? 'material-icons cursor-pointer !text-[--Primetime-main]'
    : 'material-icons cursor-pointer text-[--Dark-Grey]';

  const title = hasExpiry
    ? (isFuture
      ? t('common:urls:active_until', { date: formatExpiryDate(expiresAt) })
      : t('common:urls:expired_at', { date: formatExpiryDate(expiresAt) })
    )
    : t('common:urls:expires_at');

  return (
  <FlexRow align="center"
           className="gap-[8px]">
    <span className={`${iconClass} !text-[22px]`}
          title={title}
          onClick={toggleExpanded}>
      calendar_month
    </span>
  {expanded &&
    <input type="datetime-local"
           value={expiryToLocalDateTime(expiresAt)}
           min={expiryToLocalDateTime(Math.floor(Date.now() / 1000))}
           disabled={props.isReadOnly}
           onChange={(e) => onChange(e.target.value)}
           className="absolute ml-[30px] z-10 border-solid border-1 border-[--Mid-Grey] rounded-[4px] px-[8px] py-[4px] text-[12px] bg-white shadow-md" />
  }
  </FlexRow>
  );
};

export interface InlineActivationPickerProps {
  activatesAt:			number;
  setActivatesAt:		(ts: number) => any;
  isReadOnly?:			boolean;
}

export const InlineActivationPicker = (props: InlineActivationPickerProps) => {
  const { t } = useTranslation();

  const activatesAt = props.activatesAt || 0;
  const hasActivation = activatesAt > 0;
  const nowSec = Math.floor(Date.now() / 1000);
  const isFuture = activatesAt > nowSec;

  const [expanded, setExpanded] = React.useState(hasActivation);

  React.useEffect(() => {
    if (hasActivation)
      setExpanded(true);
  }, [hasActivation]);

  const toggleExpanded = () => {
    if (props.isReadOnly)
      return;
    setExpanded((v) => !v);
  };

  const onChange = (val: string) => {
    if (props.isReadOnly)
      return;
    props.setActivatesAt(localDateTimeToExpiry(val));
  };

  const iconClass = hasActivation
    ? 'material-icons cursor-pointer !text-[#4CAF50]'
    : 'material-icons cursor-pointer text-[--Dark-Grey]';

  const title = hasActivation
    ? (isFuture
      ? t('common:urls:activates_at_future', { date: formatExpiryDate(activatesAt) })
      : t('common:urls:activates_at_past', { date: formatExpiryDate(activatesAt) })
    )
    : t('common:urls:activates_at');

  return (
  <FlexRow align="center"
           className="gap-[8px]">
    <span className={`${iconClass} !text-[22px]`}
          title={title}
          onClick={toggleExpanded}>
      { hasActivation ? 'alarm_on' : 'alarm_off' }
    </span>
  {hasActivation && isFuture && !expanded &&
    <span className="text-[11px] text-[#4CAF50] whitespace-nowrap">
      { t('common:urls:activates_at_future', { date: formatExpiryDate(activatesAt) }) }
    </span>
  }
  {expanded &&
    <input type="datetime-local"
           value={expiryToLocalDateTime(activatesAt)}
           min={expiryToLocalDateTime(Math.floor(Date.now() / 1000))}
           disabled={props.isReadOnly}
           onChange={(e) => onChange(e.target.value)}
           className="absolute ml-[30px] z-10 border-solid border-1 border-[--Mid-Grey] rounded-[4px] px-[8px] py-[4px] text-[12px] bg-white shadow-md" />
  }
  </FlexRow>
  );
};

export interface InlineSchedulePickerProps {
  isActive:				boolean;
  activatesAt:			number;
  setActivatesAt:		(ts: number) => any;
  expiresAt:			number;
  setExpiresAt:			(ts: number) => any;
  isReadOnly?:			boolean;
}

export const InlineSchedulePicker = (props: InlineSchedulePickerProps) => {
  const { t } = useTranslation();

  // isActive: ON → show alarm_off (schedule expiry), OFF → show alarm_on (schedule activation)
  const icon = props.isActive ? 'alarm_off' : 'alarm_on';
  const ts = props.isActive ? (props.expiresAt || 0) : (props.activatesAt || 0);
  const hasTimer = ts > 0;
  const nowSec = Math.floor(Date.now() / 1000);
  const isFuture = ts > nowSec;

  const [expanded, setExpanded] = React.useState(hasTimer);

  React.useEffect(() => {
    if (hasTimer)
      setExpanded(true);
  }, [hasTimer]);

  const toggleExpanded = () => {
    if (props.isReadOnly)
      return;
    setExpanded((v) => !v);
  };

  const onChange = (val: string) => {
    if (props.isReadOnly)
      return;
    const newTs = localDateTimeToExpiry(val);
    if (props.isActive)
      props.setExpiresAt(newTs);
    else
      props.setActivatesAt(newTs);
  };

  const color = props.isActive ? '!text-[--Primetime-main]' : '!text-[#4CAF50]';
  const dateColor = props.isActive ? 'text-[--Primetime-main]' : 'text-[#4CAF50]';

  const title = hasTimer
    ? (props.isActive
      ? (isFuture
        ? t('common:urls:active_until', { date: formatExpiryDate(ts) })
        : t('common:urls:expired_at', { date: formatExpiryDate(ts) })
      )
      : (isFuture
        ? t('common:urls:activates_at_future', { date: formatExpiryDate(ts) })
        : t('common:urls:activates_at_past', { date: formatExpiryDate(ts) })
      )
    )
    : (props.isActive
      ? t('common:urls:expires_at')
      : t('common:urls:activates_at')
    );

  return (
  <FlexRow align="center"
           className="gap-[8px]">
    <span className={`material-icons cursor-pointer !text-[22px] ${hasTimer ? color : 'text-[--Dark-Grey]'}`}
          title={title}
          onClick={toggleExpanded}>
      { icon }
    </span>
  {expanded &&
    <input type="datetime-local"
           value={expiryToLocalDateTime(ts)}
           min={expiryToLocalDateTime(Math.floor(Date.now() / 1000))}
           disabled={props.isReadOnly}
           onChange={(e) => onChange(e.target.value)}
           className="absolute ml-[30px] z-10 border-solid border-1 border-[--Mid-Grey] rounded-[4px] px-[8px] py-[4px] text-[12px] bg-white shadow-md" />
  }
  {!expanded && hasTimer &&
    <span className={`text-[11px] ${dateColor} whitespace-nowrap cursor-pointer`}
          onClick={toggleExpanded}>
      { formatExpiryDate(ts) }
    </span>
  }
  </FlexRow>
  );
};

export interface UrlRowProps {
  url:					UrlInfo;
  setUrl:				(url: UrlInfo) => any;
  triedSave:			boolean;
  requestDelete:		() => any;
}
//extends UrlRowProps

export const UrlRow = (props: UrlRowProps) => {
  const { t } = useTranslation();
  //console.log("UrlRow/props=", props);

  const updateType = (val: string) => {
    let url = copyUrlInfo(props.url);
    url.type = val;

    props.setUrl(url);
  };

  const updateUrl = (val: string) => {
    let url = copyUrlInfo(props.url);
    url.url = val;

    props.setUrl(url);
  };

  const updateExpiresAt = (ts: number) => {
    let url = copyUrlInfo(props.url);
    url.expires_at = ts > 0 ? ts : 0;
    props.setUrl(url);
  };

  const updateActivatesAt = (ts: number) => {
    let url = copyUrlInfo(props.url);
    url.activates_at = ts > 0 ? ts : 0;
    props.setUrl(url);
  };

  const input =	/*props.url.url.indexOf("\n") > -1
  				?	<Input label={t('common:urls:url')}
						   value={props.url.url}
						   onValueChange={updateUrl} />
				:*/	<TextArea value={props.url.url}
						      onValueChange={updateUrl}
						      label={t('common:urls:url')}
						      showChars={false}
						      classNames={{
						        inputWrapper: "min-h-[40px]",
						      }}
						      minRows={1} />;

  const isDateInvalid = !validateActivationExpiry(props.url);

  return (
  <FlexRow direction="column"
           className="gap-[8px]">
    <Input label={t('common:urls:type')}
		   value={props.url.type}
		   isInvalid={props.triedSave && props.url.is_invalid}
           errorMessage={t('common:urls:invalid')}
		   onValueChange={updateType} />
    { input }
    <InlineSchedulePicker isActive={props.url.url !== '' && props.url.url !== '__disabled__'}
                          activatesAt={props.url.activates_at || 0}
                          setActivatesAt={updateActivatesAt}
                          expiresAt={props.url.expires_at || 0}
                          setExpiresAt={updateExpiresAt} />
    {isDateInvalid &&
      <span className="text-[12px] text-red-500">
        { t('common:urls:activation_after_expiry') }
      </span>
    }
	<FlexRow flex={0}
	             className="gap-[5px] cursor-pointer text-[--Primetime-main]"
	             onClick={() => props.requestDelete()}>
      <span className="material-icons !text-[--Primetime-main] text-[16px]">
        remove
      </span>
      { t('common:urls:remove') }
    </FlexRow>
  </FlexRow>
  );
};

export interface UrlsEditorProps {
  urls:					UrlInfo[];
  setUrls:				(arr: UrlInfo[]) => any;
  triedSave:			boolean;
  setUrlInvalid:		(val: boolean) => any;
}

const UrlsEditor = (props: UrlsEditorProps) => {
  const { t } = useTranslation();
  
  const updateUrl = React.useCallback((i: number, urlinf: UrlInfo) => {
    if (!props.urls)
      return;
    
    let urls = copyUrls(props.urls);
    urls[i] = urlinf;
    
    let invalid = false;
    const types: string[] = [];
    
    for (let i=0; i < urls.length; i++) {
      let url = urls[i];
      
      url.is_invalid = types.indexOf(url.type) > -1;
      urls[i] = url;
      
      if (!url.is_invalid) {
        types.push(url.type);
      } else {
        invalid = true;
      }

      if (!validateActivationExpiry(url))
        invalid = true;
    }

    props.setUrlInvalid(invalid);
    
    console.log("UrlsEditor/i=" + i + "/invalid=" + invalid + "/urlinf=", urlinf, "/urls=", urls);
    props.setUrls(urls);
  }, [props.urls, props.setUrlInvalid, props.setUrls]);
  
  const deleteUrl = React.useCallback((i: number) => {
    if (!props.urls)
      return;
    
    let urls = copyUrls(props.urls);
    urls.splice(i, 1);
    
    props.setUrls(urls);
  }, [props.urls, props.setUrls]);
  
  const addUrl = React.useCallback(() => {
    if (!props.urls)
      return;
    
    let urls = copyUrls(props.urls);
    urls.push({
      type:			"",
      url:			"",
      tags:			[],
    });
    
    console.log("UrlsEditor/addUrl/urls=", urls);
    props.setUrls(urls);
  }, [props.urls, props.setUrls]);
  
  return (
  <React.Fragment>
  {props.urls && props.urls.map((url: UrlInfo, i: number) =>
	<UrlRow key={i}
            url={url}
            setUrl={(newUrl: UrlInfo) => updateUrl(i, newUrl)}
            triedSave={props.triedSave}
            requestDelete={() => deleteUrl(i)} />
  )}
    <FlexRow flex={0}
             className="gap-[5px] cursor-pointer text-[--Primetime-main]"
             onClick={() => addUrl()}>
      <span className="material-icons !text-[--Primetime-main] text-[16px]">
        add
      </span>
      { t('common:urls:add') }
    </FlexRow>
  </React.Fragment>
  );
};


export default UrlsEditor;

